import React from "react"

import type { AudioRegionReviewConfig } from "@/metrics/runtime"

import type { AudioRegionParams } from "@/components/audio-player/AudioPlayer"
import type {
  MetricOutputDisplaySource,
  RunMetricOutput,
} from "@/types/RunMetricOutput"

import { NumericalMetricRecord } from "../base/NumericalMetricRecord"
import { AnomalyEventList } from "../components/AnomalyEventList"
import { getCardStyle } from "../components/deepDiveCardStyles"
import { getStat } from "../helpers/getStat"
import { parseResultJson } from "../helpers/parseResultJson"
import { WithAudioGateMixin } from "../mixins/WithAudioGateMixin"

interface PhonemeStretchResultJson {
  stretch_event_count?: number
  stretch_total_duration_seconds?: number
  interval_details?: unknown
  high_pps_intervals?: unknown
  pps_per_interval?: unknown
  interval_size_seconds?: number
  ivr_end_time?: number
  threshold?: number
}

interface SummaryStat {
  mean?: number
  min?: number
  max?: number
  median?: number
  number_of_data_points?: number
}

interface DispersionStat {
  std_dev?: number
  variance?: number
}

interface OutlierStat {
  outlier_count?: number
}

const CATEGORY = "phoneme_stretch"
const SEVERITY_THRESHOLD = 0.5
const PPS_STAT_NAME = "phonemes_per_second"

export class PhonemeStretchMetricRecord extends WithAudioGateMixin(
  NumericalMetricRecord,
) {
  overviewSize = "medium" as const
  unit = "s"
  aggregate_units = "s"

  renderMetricType(): string {
    return "Phoneme Stretch"
  }

  _renderDeepDive(mo: RunMetricOutput): React.ReactNode {
    const rawValues = parseResultJson<PhonemeStretchResultJson>(mo)
    const result = mo.result as
      | { count?: number; frequency?: number }
      | undefined
    const typedCount = result?.count
    const typedFrequency = result?.frequency
    // Typed pps stats with legacy fallback; unit=per_second.
    const summary = getStat<SummaryStat>(mo, "summary", PPS_STAT_NAME)
    const dispersion = getStat<DispersionStat>(mo, "dispersion", PPS_STAT_NAME)
    const outliers = getStat<OutlierStat>(mo, "outliers", PPS_STAT_NAME)
    const subvalues = mo.subvalues_by_timestamp ?? []
    const stretchDuration = mo.float_value ?? 0
    const eventCount =
      typedCount ??
      rawValues?.stretch_event_count ??
      summary?.number_of_data_points ??
      subvalues.length
    const card = getCardStyle(stretchDuration, 0.5)
    const threshold = rawValues?.threshold
    const intervalSize = rawValues?.interval_size_seconds

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className={`rounded-lg p-3 ${card.bg}`}>
            <div className={card.label}>Stretch Duration</div>
            <div className={card.value}>{stretchDuration.toFixed(2)}s</div>
          </div>
          <div className={`rounded-lg p-3 ${card.bg}`}>
            <div className={card.label}>Events</div>
            <div className={card.value}>{eventCount}</div>
          </div>
        </div>

        {(summary?.mean != null ||
          dispersion?.std_dev != null ||
          outliers?.outlier_count != null ||
          typedFrequency != null) && (
          <div className="grid grid-cols-2 gap-3 text-sm">
            {summary?.mean != null && (
              <div className="rounded-lg border border-coval-border p-3">
                <div className="text-coval-text-muted">Mean pps</div>
                <div className="font-semibold">{summary.mean.toFixed(2)}</div>
              </div>
            )}
            {dispersion?.std_dev != null && (
              <div className="rounded-lg border border-coval-border p-3">
                <div className="text-coval-text-muted">Std Dev pps</div>
                <div className="font-semibold">
                  {dispersion.std_dev.toFixed(2)}
                </div>
              </div>
            )}
            {outliers?.outlier_count != null && (
              <div className="rounded-lg border border-coval-border p-3">
                <div className="text-coval-text-muted">Outliers</div>
                <div className="font-semibold">{outliers.outlier_count}</div>
              </div>
            )}
            {typedFrequency != null && (
              <div className="rounded-lg border border-coval-border p-3">
                <div className="text-coval-text-muted">Frequency</div>
                <div className="font-semibold">{typedFrequency.toFixed(2)}</div>
              </div>
            )}
          </div>
        )}

        {(threshold != null || intervalSize != null) && (
          <div className="text-xs text-coval-text-muted">
            {threshold != null && <span>Threshold: {threshold} pps</span>}
            {threshold != null && intervalSize != null && <span> · </span>}
            {intervalSize != null && <span>Interval: {intervalSize}s</span>}
          </div>
        )}

        <AnomalyEventList
          subvalues={subvalues}
          severityThreshold={SEVERITY_THRESHOLD}
          chipLabel="Stretch"
          title="Stretch Segments"
        />

        {subvalues.length === 0 && (
          <div className="text-sm text-coval-text-muted">
            No phoneme stretching detected
          </div>
        )}
      </div>
    )
  }

  getAudioRegions(mo: MetricOutputDisplaySource): AudioRegionParams[] {
    if (mo?.error_message) {
      return []
    }
    try {
      const subvalues = mo.subvalues_by_timestamp ?? []
      return subvalues
        .filter((item) => (item.float_value ?? 0) >= SEVERITY_THRESHOLD)
        .map((item, idx) => {
          const severity = item.float_value ?? 0
          const alpha = Math.min(0.2 + severity * 0.5, 0.7)
          return {
            id: `${CATEGORY}:${idx}`,
            start: item.start_offset,
            end: Math.max(item.end_offset, item.start_offset + 0.5),
            color: `rgba(59, 130, 246, ${alpha.toFixed(2)})`,
            drag: false,
            resize: false,
          }
        })
    } catch {
      return []
    }
  }

  getAudioRegionReviewConfig(): AudioRegionReviewConfig | null {
    return {
      category: CATEGORY,
      scoreType: "seconds",
      buildReviewedResultJson: ({ editedCount, score, durationSeconds }) => {
        return {
          stretch_event_count: editedCount,
          stretch_total_duration_seconds: Number(score.toFixed(2)),
          duration_seconds:
            durationSeconds != null ? Number(durationSeconds.toFixed(2)) : null,
        }
      },
    }
  }
}
